import { PlayApiProvider } from '@mogzo/play';
import { GENRE_NAMES } from '@mogzo/shared';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { PostsPanel } from '../../components/community/PostsPanel';
import { GameIcon } from '../../components/games/GameArt';
import { PlayScreen, pillClass } from '../../components/play/PlayScreen';
import { Alert, PageSpinner } from '../../components/ui';
import { fetchMoreGames, type GameCard, type LiveGame, livePlayApi } from '../../lib/games';
import { useSession } from '../../lib/session';
import {
  BeforeLaunch,
  canJoin,
  Failed,
  gameArt,
  JoinButton,
  Missing,
  useLiveGame,
  useMembership,
} from './LiveGame';

/** Other games for the side panel: its genre first, then the newest; none if they can't load. */
function useMoreGames(slug: string) {
  const [games, setGames] = useState<GameCard[]>([]);
  useEffect(() => {
    let active = true;
    fetchMoreGames(slug)
      .then((result) => active && setGames(result.games))
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [slug]);
  return games;
}

/**
 * mogzo.com/games/<address>/play: the live game on a computer, in live data, with its
 * community's posts and more games beside it. Phones and tablets play full screen
 * from the game page instead.
 */
export function LivePlayPage() {
  const { slug = '' } = useParams();
  const state = useLiveGame(slug);
  const api = useMemo(() => livePlayApi(slug), [slug]);
  if (state.status === 'loading') return <PageSpinner />;
  if (state.status === 'missing') return <Missing />;
  if (state.status === 'failed') return <Failed />;
  return (
    <PlayApiProvider api={api}>
      <LivePlay data={state.data} />
    </PlayApiProvider>
  );
}

function LivePlay({ data }: { data: LiveGame }) {
  const { comingSoon } = useSession();
  const [params] = useSearchParams();
  const home = `/games/${data.game.slug}`;
  const membership = useMembership(data);
  const moreGames = useMoreGames(data.game.slug);
  const images = useMemo(() => new Map(data.images.map((image) => [image.id, image])), [data]);
  const { listing } = data;
  const members = membership.memberCount;
  const about = [
    `by ${listing.developer || 'its developer'}`,
    listing.genre && GENRE_NAMES[listing.genre],
    data.community && `${members} member${members === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <PlayScreen
      name={data.game.name}
      playUrl={data.playUrl}
      projectId={data.game.id}
      portrait={listing.orientation === 'portrait'}
      launch={{}}
      invite={params.get('match')}
      openMatch={params.get('open')}
      isDeveloper={Boolean(data.viewer?.isDeveloper)}
      home={home}
      side={(back) => (
        <PostsPanel
          back={back}
          communityId={data.community?.id ?? null}
          path={home}
          posting={membership.joined ? (comingSoon ? 'later' : 'open') : 'none'}
          placeholder="Share a run, a tip or a question"
          games={moreGames}
        />
      )}
    >
      <div className="flex shrink-0 flex-wrap items-center gap-x-3.5 gap-y-2.5">
        <Link to={home} aria-label={`${data.game.name}’s game page`}>
          <GameIcon game={gameArt(data, images)} className="size-12 rounded-[13px] text-[17px]" />
        </Link>
        <div className="min-w-0 flex-[1_1_220px]">
          <h1 className="truncate text-xl leading-[26px] font-bold tracking-[-0.01em]">
            {data.game.name}
          </h1>
          <p className="text-sm text-muted">{about}</p>
        </div>
        <div className="flex gap-2">
          {canJoin(data) && <JoinButton data={data} membership={membership} />}
          <Link to={home} className={pillClass}>
            Game page
          </Link>
        </div>
      </div>
      {membership.error && <Alert>{membership.error}</Alert>}
      {data.viewer && (comingSoon || data.suspended) && <BeforeLaunch data={data} quiet />}
    </PlayScreen>
  );
}
